import { useEffect, useSyncExternalStore } from "react";
import {
  changeUserPreferences,
  DEFAULT_USER_PREFERENCES,
  readUserPreferences,
  sameStarredLlm,
  type UserPreferences,
  type UserPreferencesChange,
} from "../../../api/userPreferencesApi";
import { toUiError, type UiError } from "../../../errors/uiError";
import { useSession } from "../../../session/sessionContext";

/**
 * The signed-in user's view settings for the whole app (accounts.md, As built: each user's
 * view settings), read once per account and shared by every view that uses them: the
 * Workflow graph's toolbar and Profile change the same value. A change shows at once and
 * goes back if the server refuses it.
 *
 * `idle` → `loading` → `loaded` | `failed`; a change keeps the status and sets `saving`
 * until the server answers. Until loaded, views use the defaults.
 */

export type PreferencesStatus = "idle" | "loading" | "loaded" | "failed";

export interface PreferencesSnapshot {
  /** The account they belong to; `null` before anyone signed in. */
  readonly userId: string | null;
  readonly status: PreferencesStatus;
  readonly preferences: UserPreferences;
  readonly saving: boolean;
  /** The last read or change that failed; cleared by the next success. */
  readonly error: UiError | null;
}

const INITIAL: PreferencesSnapshot = { userId: null, status: "idle", preferences: DEFAULT_USER_PREFERENCES, saving: false, error: null };

let snapshot: PreferencesSnapshot = INITIAL;
const listeners = new Set<() => void>();

function update(next: PreferencesSnapshot): void {
  snapshot = next;
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reads the account's settings once; a new account starts again from the defaults. */
export function loadPreferences(userId: string): void {
  if (snapshot.userId === userId && snapshot.status !== "idle" && snapshot.status !== "failed") {
    return;
  }
  update({ ...INITIAL, userId, status: "loading" });
  readUserPreferences().then(
    (preferences) => {
      if (snapshot.userId === userId) {
        update({ ...snapshot, status: "loaded", preferences, error: null });
      }
    },
    (caught: unknown) => {
      if (snapshot.userId === userId) {
        update({ ...snapshot, status: "failed", error: toUiError(caught) });
      }
    },
  );
}

/** Shows the change at once; the server's answer replaces it, or a refusal undoes it. */
export async function changePreferences(change: UserPreferencesChange): Promise<void> {
  const before = snapshot;
  update({ ...snapshot, preferences: changed(snapshot.preferences, change), saving: true });
  try {
    const saved = await changeUserPreferences(change);
    if (snapshot.userId === before.userId) {
      update({ ...snapshot, status: "loaded", preferences: saved, saving: false, error: null });
    }
  } catch (caught) {
    if (snapshot.userId === before.userId) {
      update({ ...snapshot, preferences: before.preferences, saving: false, error: toUiError(caught) });
    }
  }
}

/** The settings as they read once `change` is kept: a star is added to or taken from the others, one agent's level set or cleared. */
function changed(preferences: UserPreferences, change: UserPreferencesChange): UserPreferences {
  const { favouriteWorkflow, starredLlm, reasoningLevel, ...settings } = change;
  const favouriteWorkflows = new Set(preferences.favouriteWorkflows);
  if (favouriteWorkflow?.favourite === true) favouriteWorkflows.add(favouriteWorkflow.workflow);
  if (favouriteWorkflow?.favourite === false) favouriteWorkflows.delete(favouriteWorkflow.workflow);
  const others = starredLlm === undefined ? preferences.starredLlms : preferences.starredLlms.filter((star) => !sameStarredLlm(star, starredLlm.llm));
  const starredLlms = starredLlm?.starred === true ? [...others, starredLlm.llm] : others;
  const otherLevels = Object.entries(preferences.reasoningLevels).filter(([agentId]) => agentId !== reasoningLevel?.agentId);
  const reasoningLevels = reasoningLevel?.effortId == null ? Object.fromEntries(otherLevels) : { ...Object.fromEntries(otherLevels), [reasoningLevel.agentId]: reasoningLevel.effortId };
  return { ...preferences, ...settings, favouriteWorkflows, starredLlms, reasoningLevels };
}

/** The signed-in user's settings, read when first needed. */
export function useUserPreferences(): PreferencesSnapshot {
  const { session } = useSession();
  const userId = session.status === "signedIn" ? session.session.account.userId : null;
  useEffect(() => {
    if (userId !== null) {
      loadPreferences(userId);
    }
  }, [userId]);
  const current = useSyncExternalStore(subscribe, () => snapshot, () => INITIAL);
  // Another account's settings never show, even for the moment before the new read starts.
  return current.userId === userId ? current : { ...INITIAL, userId };
}
